import { MediaType, Product, ProductMedia, ProductStatesModel } from '@futrli-sdk/app-store';
import { BillingModel, ProductSetup } from '@futrli-sdk/organisations';
import { differenceWith } from 'lodash';
import { IntegrationBuckets } from '../core/types';

/**
 * Checks if a product setup is premium.
 * @param {ProductSetup} productSetup
 * @return {boolean}
 */
export const isProductSetupPremium = (productSetup: ProductSetup): boolean =>
  productSetup.billing_model === BillingModel.premium;

/**
 * Checks if a product setup is free.
 * @param {ProductSetup} productSetup
 * @return {boolean}
 */
export const isProductSetupFree = (productSetup: ProductSetup): boolean =>
  productSetup.billing_model === BillingModel.free;

/**
 * Comparator beetween product and produtSetup.
 * @param {Product} product
 * @param {ProductSetup} productSetup
 * @returns {boolean}
 */
const productComparator = (product: Product, productSetup: ProductSetup) => product.id === productSetup.product_id;

/**
 * Gets the difference between the current used organisation's products and the available products in the app.
 * @param {Product[]} products
 * @param {ProductSetup[]} enabledProducts
 * @returns {Product[]} available products.
 */
export const getAvailableProducts = (products: Product[], enabledProducts: ProductSetup[]): Product[] => {
  const availableProducts = differenceWith(products, enabledProducts, productComparator);

  return availableProducts.filter((product: Product) => product.status === ProductStatesModel.active);
};

/**
 * Finds the specific media data from a product with the choosen MediaType.
 * @param {Product} product
 * @param {MediaType} mediaType
 * @returns {ProductMedia | undefined}
 */
export const findProductMedia = (product: Product, mediaType: MediaType): ProductMedia | undefined => {
  const { media = [] } = product;

  return media.find((productMedia: ProductMedia) => productMedia.type === mediaType);
};

/**
 * Checks if a given key exists in bucketed integrations, returning the first alphabetical
 * key if not.
 *
 * @param {IntegrationBuckets} buckets
 * @param {string} category
 * @returns {string}
 */
export const getCategoryOrDefault = (buckets: IntegrationBuckets, category: string): string => {
  return buckets && !buckets.hasOwnProperty(category) ? Object.keys(buckets).sort()[0] : category;
};
